import React, { useCallback, useContext, useEffect, useState } from "react";
import styled from "styled-components";

import { Button, FormButton } from "@/components/Form";
import ResultGlass, { ResultType, SharingType } from "@/components/ResultGlass";
import { GlobalContext } from "@/contexts";
import LocaleContext from "@/i18n";
import { OutroType, RegistrationStatus } from "@/service/Model";
import { handleShare } from "@/utils/socialActions";

type QuizSummaryProps = {
  icons: IIcons;
  userVoteCount: number;
  totalVoteCount: number;
  showDetails: boolean;
  sharingTitle: string;
  onRetry: () => void;
  onLoad: () => void;
  outroType?: OutroType;
};

export const QuizSummary: React.FC<QuizSummaryProps> = (
  props: QuizSummaryProps
): React.JSX.Element => {
  const [showResult, setShowResult] = useState<boolean>(false);
  const [enableRetry, setEnableRetry] = useState<boolean>(false);

  const globalContext = useContext(GlobalContext);
  const localeContext = useContext(LocaleContext);

  const {
    userVoteCount,
    totalVoteCount,
    onRetry,
    sharingTitle,
    showDetails,
    icons,
    onLoad,
    outroType,
  } = props;

  const hasOutro = outroType !== undefined;
  const hasRegistrationForm = outroType === OutroType.RegistrationForm;
  const hasRegistered =
    globalContext.registrationStatus === RegistrationStatus.Success;

  useEffect(() => {
    setTimeout(() => onLoad(), 4000);
    // Activate the retry button after the result glass animation and outro have been displayed
    setTimeout(() => setEnableRetry(true), hasOutro ? 4800 : 1500);
    setTimeout(() => setShowResult(true), 1000);
  }, []);

  const hasNotAllCorrectAnswers = userVoteCount !== totalVoteCount;

  const handleShareClick = useCallback((type: SharingType) => {
    handleShare(type, sharingTitle, globalContext.brandSettings?.xHandle);
  }, []);

  const renderResults = (): React.JSX.Element => (
    <>
      <SummaryBox>
        <ResultGlass
          type={ResultType.count}
          resultText={localeContext.translation.quizSummary.resultText}
          onShareClick={handleShareClick}
          showDetails={showDetails}
          icons={icons}
          userVoteCount={userVoteCount}
          totalVoteCount={totalVoteCount}
        />
      </SummaryBox>
      {hasNotAllCorrectAnswers && (
        <FormButton
          type="button"
          label={localeContext.translation.tryAgain}
          disabled={!enableRetry}
          onClick={onRetry}
        />
      )}
      {hasRegistrationForm && (
        <Button onClick={onLoad} disabled={hasRegistered}>
          {localeContext.translation.registerAgain}
        </Button>
      )}
    </>
  );

  return <SummaryContent>{showResult && renderResults()}</SummaryContent>;
};

const SummaryContent = styled.div`
  min-height: 341px;
  width: 90%;
  margin: 0 auto;
`;

const SummaryBox = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;
